import { ExpandingTextInput } from './ExpandingTextInput';
import { ChecklistGpsAddressField } from './ChecklistGpsAddressField';
import { isGpsAddressField } from '@/services/checklist/gpsAddress';
import { useGpsAddressCapability } from '@/hooks/useGpsAddressCapability';
import { countChecklistFilledFields, getChecklistReturnGroup, groupChecklistRows, type ChecklistLayoutRow } from '@/services/checklist/checklistLayout';
import { observationKey, observationFieldId } from '@/services/checklist/checklistObservations';
import { ChecklistDraftPersistence, reconcileChecklistMediaDraft } from '@/services/checklist/ChecklistDraftPersistence';
import { subscribeChecklistMediaTerminal, type ChecklistMediaTerminalEvent } from '@/services/checklist/ChecklistMediaTerminalEvents';
import React, { useMemo, useRef, useEffect } from 'react';
import {
  Keyboard,
  NativeScrollEvent,
  NativeSyntheticEvent,
  Platform,
  ScrollView,
  Text,
  TouchableOpacity,
  StyleSheet,
  View,
  useWindowDimensions
} from 'react-native';
import { useObservable } from '@legendapp/state/react';
// @ts-ignore
import { groupBy } from 'lodash'; // Se faltar tipagem: npm i --save-dev @types/lodash
import { databaseService } from '../../services/databaseService';
import { ChecklistFieldsFormLogger } from '../../utils/logger';
import ChecklistPhotoField from './ChecklistPhotoField';
import ChecklistSignatureField from './ChecklistSignatureField';
import ChecklistMarkField from './ChecklistMarkField';
import ChecklistMediaObservation from './ChecklistMediaObservation';
import type { ChecklistMarkPoint } from '@/services/checklist/checklistMarking';
import { CHECKLIST_UNSAVED_MARKS_MESSAGE } from '@/services/checklist/checklistMarking';
import { debugPause } from '@/utils/debugPauseUniversal';
import ChecklistDropdownField from './ChecklistDropdownField';
import theme from '../../config/theme';
import { Ionicons } from '@expo/vector-icons';
import ChecklistCEPField from './ChecklistCEPField';
import ChecklistDateTimeField from './ChecklistDateTimeField';
import FormattedInput from '../FormattedInput';
import ChecklistReadyMessageField from './ChecklistReadyMessageField';
import { traceImage } from '@/utils/debugImageTrace';
import { APP_CONSTANTS } from '@/config/constants';
import { getSyncClientBuild } from '@/services/sync/SyncCanaryFeatureService';

interface ChecklistField {
  endereco_gps?: number | boolean | null;
  historical_read_only?: boolean;
  id: string;
  original_campo_id?: string | null;
  grupo: string;
  nome: string;
  tipo: string;
  ordem: number;
}

interface ChecklistFieldsFormProps {
  checklistId: string;
  empresaId: string;
  template: ChecklistField[];
  editable?: boolean;
  observationsEnabled?: boolean;
  draft?: ReturnType<typeof useObservable<Record<string, string>>>;
  onPendingChange?: (pending: boolean) => void;
  onSaveError?: (message: string | null) => void;
  onDraftFlushed?: (snapshot: Record<string, string>) => void;
  onActiveGroupChange?: (group: string) => void;
  onReturnGroupChange?: (group: string | null) => void;
  onGroupExpansionChange?: (state: { allExpanded: boolean; allCollapsed: boolean; captureBusy: boolean }) => void;
  onPhotoFlowStateChange?: (state: {
    fieldId: string;
    active: boolean;
    reason: string;
    at: number;
  }) => void;
  onUnsavedMarkChange?: (unsaved: boolean) => void;
  onMarkInteraction?: (kind: 'tap' | 'drag') => boolean;
}

export interface ChecklistFieldsFormHandle {
  flushPending: () => Promise<void>;
  scrollToGroup: (group: string) => void;
  scrollToField: (fieldId: string) => void;
  setAllGroupsExpanded: (expanded: boolean) => void;
}

const CHECKLIST_FIELD_LOG_SAMPLE_LIMIT = 3;
const IOS_GROUP_SCROLL_TOP_OFFSET = 12;
const GROUP_SCROLL_RETRY_DELAY_MS = 90;

const formatValueForLog = (value?: string | null): string => {
  if (value === null || value === undefined) return '<sem-valor>';
  const stringValue = String(value);
  if (stringValue.length === 0) return '<vazio>';
  if (stringValue.length > 60) {
    return `${stringValue.slice(0, 57)}...`;
  }
  return stringValue;
};

const getChecklistLogConfig = (): { ENABLED: boolean; VERBOSE?: boolean } => {
  const moduleConfig = APP_CONSTANTS.DEBUG.MODULES['CHECKLIST-FIELDS-FORM' as keyof typeof APP_CONSTANTS.DEBUG.MODULES];
  return (moduleConfig ?? { ENABLED: false, VERBOSE: false }) as { ENABLED: boolean; VERBOSE?: boolean };
};

// Novo componente de texto controlado e memoizado para evitar remounts desnecessários
interface TextFieldProps {
  field: ChecklistField;
  value: string;
  onChange: (val: string) => void;
  editable?: boolean;
  hideLabel?: boolean;
}

const TextField: React.FC<TextFieldProps> = React.memo(({ field, value, onChange, editable = true, hideLabel = false }) => (
  <View style={styles.itemContainer}>
    {!hideLabel && <Text style={styles.label}>{field.nome}</Text>}
    <ExpandingTextInput
      style={styles.input}
      value={value}
      onChangeText={onChange}
      editable={editable}
      placeholder={field.nome}
      placeholderTextColor={theme.colors.text.tertiary}
      autoCorrect={false}
      autoCapitalize="sentences"
    />
  </View>
));

// Novo componente para campos numéricos
const NumberField: React.FC<TextFieldProps> = React.memo(({ field, value, onChange, editable = true, hideLabel = false }) => {
  const [height, setHeight] = React.useState(42);
  return <View style={styles.itemContainer}>
    {!hideLabel && <Text style={styles.label}>{field.nome}</Text>}
    <FormattedInput
      style={[styles.input, { minHeight: 42, ...(Platform.OS === 'ios' ? {} : { height }) }]}
      value={value}
      onChangeText={onChange}
      editable={editable}
      multiline
      scrollEnabled={false}
      textAlignVertical="top"
      onContentSizeChange={event => setHeight(Math.max(42, Math.ceil(event.nativeEvent.contentSize.height)))}
      type="number"
      placeholder="Número"
    />
  </View>;
});

/**
 * Formulário de campos do checklist (apenas campos de texto)
 * Salva localmente a cada digitação (offline-first), com debounce de escrita no banco
 */
type FormRow = ChecklistLayoutRow<ChecklistField>;
type ChecklistSection = { title: string; data: FormRow[] };

export const ChecklistFieldsForm = React.forwardRef<ChecklistFieldsFormHandle, ChecklistFieldsFormProps>(({
  checklistId,
  empresaId,
  template,
  editable = true,
  observationsEnabled = false,
  draft: externalDraft,
  onPendingChange,
  onSaveError,
  onDraftFlushed,
  onActiveGroupChange,
  onReturnGroupChange,
  onGroupExpansionChange,
  onPhotoFlowStateChange,
  onUnsavedMarkChange,
  onMarkInteraction,
}, ref) => {
  const gpsEnabled = useGpsAddressCapability(empresaId);
  // Estado observável para os valores dos campos (pode vir de fora)
  const internalDraft = useObservable<Record<string, string>>({});
  const draft = externalDraft ?? internalDraft;
  // Instância do serviço de banco de dados
  const db = databaseService;
  // Buffer de campos alterados persiste entre renders
  const persistence = React.useMemo(() => new ChecklistDraftPersistence(), [checklistId, empresaId]);
  const currentPersistenceRef = useRef(persistence);
  currentPersistenceRef.current = persistence;
  const callbacksRef = useRef({ onPendingChange, onSaveError, onDraftFlushed });
  callbacksRef.current = { onPendingChange, onSaveError, onDraftFlushed };
  const dirtyRef = { current: persistence.dirty };
  const mounted = useRef(true);
  const [draftMarksByField, setDraftMarksByField] = React.useState<Record<string, ChecklistMarkPoint[]>>({});
  const draftMarksByFieldRef = useRef(draftMarksByField);
  draftMarksByFieldRef.current = draftMarksByField;
  const terminalMediaRef = useRef<Record<string, ChecklistMediaTerminalEvent>>({});
  const applyTerminalMedia = React.useCallback((event: ChecklistMediaTerminalEvent) => {
    if (event.companyId !== empresaId || event.checklistId !== checklistId) return;
    const changes = reconcileChecklistMediaDraft(draft.peek(), persistence.dirty,
      draftMarksByFieldRef.current[event.fieldId] ?? [], event);
    if (!changes) {
      if (draftMarksByFieldRef.current[event.fieldId]?.length) terminalMediaRef.current[event.fieldId] = event;
      return;
    }
    delete terminalMediaRef.current[event.fieldId];
    draft.set({ ...draft.peek(), ...changes });
    callbacksRef.current.onDraftFlushed?.(changes);
  }, [empresaId, checklistId, draft, persistence]);
  useEffect(() => {
    terminalMediaRef.current = {};
    return subscribeChecklistMediaTerminal(applyTerminalMedia);
  }, [applyTerminalMedia]);
  useEffect(() => {
    Object.values(terminalMediaRef.current).forEach(applyTerminalMedia);
  }, [draftMarksByField, applyTerminalMedia]);
  const hasUnsavedMarks = Object.values(draftMarksByField).some(points => points.length > 0);
  const hasUnsavedMarksRef = useRef(hasUnsavedMarks);
  hasUnsavedMarksRef.current = hasUnsavedMarks;
  const flushPendingRef = useRef<() => Promise<void>>(async () => {});
  const previousDraftRef = useRef<Record<string, string | undefined>>({});
  const missingValueLoggedRef = useRef<Set<string>>(new Set());
  const sectionHeaderOffsetsRef = useRef<Record<string, number>>({});
  const iosSectionHeaderRefs = useRef<Record<string, View | null>>({});
  const pendingScrollGroupRef = useRef<string | null>(null);
  const fieldNodesRef = useRef<Record<string, View | null>>({});
  const scrollViewRef = useRef<ScrollView>(null);
  const chipsRef = useRef<ScrollView>(null);
  const chipOffsetsRef = useRef<Record<string, number>>({});
  const chipJumpRef = useRef(false);
  const chipRequestedGroupRef = useRef<string | null>(null);
  const navigationEpochRef = useRef(0);
  const chipJumpTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const [activeGroup, setActiveGroup] = React.useState<string | null>(null);
  const iosCurrentScrollYRef = useRef(0);
  const viewportHeightRef = useRef(0);
  const contentHeightRef = useRef(0);
  // Reavaliar o formulário após alterações do draft sem remontar os campos.
  const [, setListVersion] = React.useState(0);
  const FLUSH_DELAY = 1000;
  const FLUSH_DELAY_WITH_UPLOAD = 1000; // pode ser usado para Delay maior para uploads de imagem (não está sendo usado)
  const flushTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);

  const { VERBOSE: verboseChecklistLogsEnabled = false } = getChecklistLogConfig();
  const lastReportedGroupRef = useRef<string | null>(null);
  const lastReturnGroupRef = useRef<string | null | undefined>(undefined);
  const [expandedGroups, setExpandedGroups] = React.useState<Record<string, boolean>>({});
  const groupHeightsRef = useRef<Record<string, number>>({});
  const capturingFieldsRef = useRef(new Set<string>());
  const [captureBusy, setCaptureBusy] = React.useState(false);
  const pendingGlobalCollapseRef = useRef(false);
  const pendingCollapseGroupsRef = useRef(new Set<string>());
  const [labelHeights, setLabelHeights] = React.useState<Record<string, number>>({});
  const headerHeightsRef = useRef<Record<string, number>>({});

  // Spinner suave: delay de 120 ms
  const spinnerTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const showSpinner = React.useCallback(() => {
    onPendingChange?.(true);
    if (spinnerTimer.current) return;
    spinnerTimer.current = setTimeout(() => {
      spinnerTimer.current = undefined;
      if (mounted.current && currentPersistenceRef.current === persistence && persistence.hasPending()) callbacksRef.current.onPendingChange?.(true);
    }, 120);
  }, [onPendingChange, persistence]);
  const hideSpinner = React.useCallback(() => {
    if (spinnerTimer.current) {
      clearTimeout(spinnerTimer.current);
      spinnerTimer.current = undefined;
    }
    callbacksRef.current.onPendingChange?.(false);
  }, []);

  // 1. Mantém a ordem já aplicada em useChecklistEditor (ordenada por `ordem`)
  //    Isso preserva também a sequência dos grupos conforme o primeiro campo
  const { fontScale } = useWindowDimensions();
  const columns = fontScale > 1.25 ? 1 : 2;
  const observationBuild = getSyncClientBuild();
  const canObserve = React.useCallback((field: ChecklistField) => {
    if (!observationsEnabled || !editable || field.historical_read_only) return false;
    if (field.tipo.startsWith('dropdown')) return observationBuild >= 128;
    return observationBuild >= 130 && ['foto', 'photo', 'marcação', 'marcacao', 'mark'].includes(field.tipo);
  }, [observationsEnabled, editable, observationBuild]);
  const orderedFields = useMemo<ChecklistField[]>(() => template, [template]);

  // 2. Agrupar por grupo sem alterar a sequência do template
  const sections: ChecklistSection[] = useMemo(
    () => Object.entries(groupBy(orderedFields, 'grupo'))
      .map(([title, data]) => ({ title, data: groupChecklistRows(data as ChecklistField[], columns) })),
    [orderedFields, columns]
  );

  const reportActiveGroup = React.useCallback((group?: string) => {
    if (!group) return;
    if (!sections.some(section => section.title === group)) return;
    if (pendingScrollGroupRef.current === group) {
      pendingScrollGroupRef.current = null;
    }
    if (group === lastReportedGroupRef.current) return;
    lastReportedGroupRef.current = group;
    setActiveGroup(group);
    ChecklistFieldsFormLogger.Main.info(`[activeGroup] os=${Platform.OS} grupo=${group}`);
    onActiveGroupChange?.(group);
  }, [onActiveGroupChange, sections]);

  const allExpanded = sections.every(section => expandedGroups[section.title] !== false);
  const allCollapsed = sections.length > 0 && sections.every(section => expandedGroups[section.title] === false);
  useEffect(() => { onGroupExpansionChange?.({ allExpanded, allCollapsed, captureBusy }); }, [allExpanded, allCollapsed, captureBusy, onGroupExpansionChange]);
  const cancelChipNavigation = React.useCallback(() => {
    navigationEpochRef.current += 1;
    if (Platform.OS === 'web' && chipJumpRef.current) {
      scrollViewRef.current?.scrollTo({ y: iosCurrentScrollYRef.current, animated: false });
    }
    chipRequestedGroupRef.current = null;
    chipJumpRef.current = false;
    pendingScrollGroupRef.current = null;
    if (chipJumpTimerRef.current) clearTimeout(chipJumpTimerRef.current);
  }, []);
  const webScrollIntentProps = Platform.OS === 'web' ? {
    onWheel: cancelChipNavigation,
    onTouchMove: cancelChipNavigation,
    onPointerDown: (event: { target: unknown; currentTarget: unknown }) => {
      if (event.target === event.currentTarget) cancelChipNavigation();
    },
    onKeyDown: (event: { key: string; target: { tagName?: string; isContentEditable?: boolean } }) => {
      if (['ArrowUp', 'ArrowDown', 'PageUp', 'PageDown', 'Home', 'End', ' '].includes(event.key) &&
        !['INPUT', 'TEXTAREA', 'SELECT'].includes(event.target.tagName ?? '') && !event.target.isContentEditable) {
        cancelChipNavigation();
      }
    },
  } : {};
  useEffect(() => {
    if (!sections.some(section => section.title === activeGroup)) {
      cancelChipNavigation();
      reportActiveGroup(sections[0]?.title);
    }
  }, [sections, activeGroup, reportActiveGroup, cancelChipNavigation]);
  useEffect(() => {
    if (activeGroup) chipsRef.current?.scrollTo({ x: Math.max(0, (chipOffsetsRef.current[activeGroup] ?? 0) - 70), animated: true });
  }, [activeGroup]);

  useEffect(() => {
    // Preserva offsets já medidos e remove apenas grupos que deixaram de existir.
    const validGroups = new Set(sections.map(section => section.title));
    Object.keys(sectionHeaderOffsetsRef.current).forEach(group => {
      if (!validGroups.has(group)) {
        delete sectionHeaderOffsetsRef.current[group];
      }
    });
    Object.keys(iosSectionHeaderRefs.current).forEach(group => {
      if (!validGroups.has(group)) {
        delete iosSectionHeaderRefs.current[group];
      }
    });
  }, [sections]);

  const updateScrollGroups = React.useCallback(() => {
    const scrollY = iosCurrentScrollYRef.current;
    const geometry = sections.map(section => ({
      title: section.title, expanded: expandedGroups[section.title] !== false,
      top: sectionHeaderOffsetsRef.current[section.title],
      height: groupHeightsRef.current[section.title] ?? 0,
      headerHeight: headerHeightsRef.current[section.title] ?? 0,
    }));
    const anchorY = scrollY + IOS_GROUP_SCROLL_TOP_OFFSET;
    const atBottom = scrollY > 0 && viewportHeightRef.current > 0 &&
      scrollY + viewportHeightRef.current >= contentHeightRef.current - 2;
    const current = atBottom ? geometry[geometry.length - 1] :
      geometry.find(group => Number.isFinite(group.top) && anchorY >= group.top && anchorY < group.top + group.height);
    // Layout, teclado e limite inferior não devem substituir o destino escolhido no chip.
    if (current && !chipJumpRef.current && !chipRequestedGroupRef.current) reportActiveGroup(current.title);
    const returnGroup = getChecklistReturnGroup(geometry, scrollY);
    if (returnGroup !== lastReturnGroupRef.current) {
      lastReturnGroupRef.current = returnGroup;
      onReturnGroupChange?.(returnGroup);
    }
  }, [sections, expandedGroups, reportActiveGroup, onReturnGroupChange]);
  useEffect(() => { updateScrollGroups(); }, [updateScrollGroups]);

  const setIosSectionHeaderRef = React.useCallback((group: string, node: View | null) => {
    if (node) {
      iosSectionHeaderRefs.current[group] = node;
      return;
    }
    delete iosSectionHeaderRefs.current[group];
  }, []);

  const performIosScrollToOffset = React.useCallback((
    group: string,
    origin: 'direct' | 'retry' | 'layout' | 'measure',
    offset: number
  ) => {
    if (!scrollViewRef.current) {
      ChecklistFieldsFormLogger.Main.warn(
        `[scrollToGroup] scrollView indisponível os=ios grupo=${group} origem=${origin}`
      );
      return;
    }

    Keyboard.dismiss();
    const targetY = Math.max(0, offset - IOS_GROUP_SCROLL_TOP_OFFSET);
    ChecklistFieldsFormLogger.Main.info(
      `[scrollToGroup] os=ios grupo=${group} origem=${origin} metodo=scrollView offset=${Math.round(offset)} target=${Math.round(targetY)}`
    );
    scrollViewRef.current.scrollTo({ y: targetY, animated: true });
    pendingScrollGroupRef.current = null;
  }, []);

  const measureIosSectionOffset = React.useCallback((
    group: string,
    origin: 'direct' | 'retry'
  ) => {
    const navigationEpoch = navigationEpochRef.current;
    const headerNode = iosSectionHeaderRefs.current[group];
    const scrollNode = scrollViewRef.current as (ScrollView & {
      measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void;
    }) | null;

    if (
      !headerNode ||
      !scrollNode ||
      typeof headerNode.measureInWindow !== 'function' ||
      typeof scrollNode.measureInWindow !== 'function'
    ) {
      ChecklistFieldsFormLogger.Main.warn(
        `[scrollToGroup] measure indisponível os=ios grupo=${group} origem=${origin}`
      );
      return false;
    }

    scrollNode.measureInWindow((_scrollX, scrollY) => {
      if (!mounted.current || navigationEpoch !== navigationEpochRef.current) return;
      headerNode.measureInWindow((_headerX, headerY, _width, _height) => {
        if (!mounted.current || navigationEpoch !== navigationEpochRef.current) return;
        if (!Number.isFinite(scrollY) || !Number.isFinite(headerY)) {
          ChecklistFieldsFormLogger.Main.warn(
            `[scrollToGroup] measure inválido os=ios grupo=${group} origem=${origin}`
          );
          return;
        }

        const measuredOffset = Math.max(
          0,
          Math.round(iosCurrentScrollYRef.current + (headerY - scrollY))
        );
        sectionHeaderOffsetsRef.current[group] = measuredOffset;
        ChecklistFieldsFormLogger.Main.info(
          `[scrollToGroup] measureReady os=ios grupo=${group} origem=${origin} offset=${measuredOffset}`
        );
        requestAnimationFrame(() => {
          if (mounted.current && navigationEpoch === navigationEpochRef.current) performIosScrollToOffset(group, 'measure', measuredOffset);
        });
      });
    });

    return true;
  }, [performIosScrollToOffset]);

  const scrollToGroupOnIos = React.useCallback((group: string, origin: 'direct' | 'retry' | 'layout' | 'measure') => {
    const navigationEpoch = navigationEpochRef.current;
    const offset = sectionHeaderOffsetsRef.current[group];
    if (!scrollViewRef.current) {
      ChecklistFieldsFormLogger.Main.warn(
        `[scrollToGroup] scrollView indisponível os=ios grupo=${group} origem=${origin}`
      );
      return;
    }

    if (!Number.isFinite(offset)) {
      ChecklistFieldsFormLogger.Main.warn(
        `[scrollToGroup] offset indisponível os=ios grupo=${group} origem=${origin}`
      );
      const startedMeasurement =
        (origin === 'direct' || origin === 'retry') && measureIosSectionOffset(group, origin);
      if (!startedMeasurement && origin === 'direct') {
        setTimeout(() => {
          if (mounted.current && navigationEpoch === navigationEpochRef.current) scrollToGroupOnIos(group, 'retry');
        }, GROUP_SCROLL_RETRY_DELAY_MS);
      }
      return;
    }

    performIosScrollToOffset(group, origin, offset);
  }, [measureIosSectionOffset, performIosScrollToOffset]);

  const handleIosSectionLayout = React.useCallback((group: string, offsetY: number) => {
    const normalizedOffset = Math.max(0, Math.round(offsetY));
    const previousOffset = sectionHeaderOffsetsRef.current[group];
    if (previousOffset === normalizedOffset) return;

    sectionHeaderOffsetsRef.current[group] = normalizedOffset;

    if (pendingScrollGroupRef.current === group) {
      const navigationEpoch = navigationEpochRef.current;
      ChecklistFieldsFormLogger.Main.info(
        `[scrollToGroup] layoutReady os=ios grupo=${group} offset=${normalizedOffset}`
      );
      requestAnimationFrame(() => {
        if (navigationEpoch === navigationEpochRef.current && pendingScrollGroupRef.current === group) scrollToGroupOnIos(group, 'layout');
      });
    }
  }, [scrollToGroupOnIos]);

  const handleIosScroll = React.useCallback((event: NativeSyntheticEvent<NativeScrollEvent>) => {
    iosCurrentScrollYRef.current = event.nativeEvent.contentOffset.y;
    updateScrollGroups();
  }, [updateScrollGroups]);

  const openGroup = React.useCallback((group: string) => {
    pendingCollapseGroupsRef.current.delete(group);
    setExpandedGroups(previous => previous[group] !== false ? previous : { ...previous, [group]: true });
  }, []);

  const navigateToGroup = React.useCallback((group: string) => {
    if (capturingFieldsRef.current.size) return;
    if (!sections.some(section => section.title === group)) return;
    cancelChipNavigation();
    Keyboard.dismiss();
    openGroup(group);
    chipRequestedGroupRef.current = group;
    chipJumpRef.current = true;
    if (chipJumpTimerRef.current) clearTimeout(chipJumpTimerRef.current);
    chipJumpTimerRef.current = setTimeout(() => {
      chipJumpRef.current = false;
      if (mounted.current) {
        updateScrollGroups();
      }
    }, 650);
    reportActiveGroup(group);
    pendingScrollGroupRef.current = group;
    requestAnimationFrame(() => {
      if (chipRequestedGroupRef.current === group) scrollToGroupOnIos(group, 'direct');
    });
  }, [sections, openGroup, reportActiveGroup, scrollToGroupOnIos, updateScrollGroups, cancelChipNavigation]);

  const setAllGroupsExpanded = React.useCallback((expanded: boolean) => {
    Keyboard.dismiss();
    // A captura mantém também a posição e a geometria dos demais cartões até terminar.
    if (!expanded && capturingFieldsRef.current.size) {
      pendingGlobalCollapseRef.current = true;
      return;
    }
    pendingGlobalCollapseRef.current = false;
    cancelChipNavigation();
    const next: Record<string, boolean> = {};
    sections.forEach(section => {
      const capturing = template.some(field => field.grupo === section.title && capturingFieldsRef.current.has(field.id));
      if (!expanded && capturing) {
        pendingCollapseGroupsRef.current.add(section.title);
        next[section.title] = true;
      } else {
        pendingCollapseGroupsRef.current.delete(section.title);
        next[section.title] = expanded;
      }
    });
    setExpandedGroups(next);
    if (!expanded) scrollViewRef.current?.scrollTo({ y: 0, animated: true });
  }, [sections, template, cancelChipNavigation]);

  const scrollToMeasuredField = React.useCallback((fieldId: string, group: string, retry = false) => {
    const navigationEpoch = navigationEpochRef.current;
    const node = fieldNodesRef.current[fieldId];
    const scroll = scrollViewRef.current as (ScrollView & { measureInWindow?: (callback: (x: number, y: number, width: number, height: number) => void) => void }) | null;
    if (!node || !scroll || typeof node.measureInWindow !== 'function' || typeof scroll.measureInWindow !== 'function') {
      if (!retry) setTimeout(() => { if (mounted.current && navigationEpoch === navigationEpochRef.current) scrollToMeasuredField(fieldId, group, true); }, GROUP_SCROLL_RETRY_DELAY_MS);
      else scrollToGroupOnIos(group, 'retry');
      return;
    }
    Keyboard.dismiss();
    scroll.measureInWindow((_sx, sy) => node.measureInWindow((_fx, fy) => {
      if (!mounted.current || navigationEpoch !== navigationEpochRef.current || !Number.isFinite(sy) || !Number.isFinite(fy)) return;
      performIosScrollToOffset(group, 'measure', iosCurrentScrollYRef.current + fy - sy);
    }));
  }, [performIosScrollToOffset, scrollToGroupOnIos]);

  React.useImperativeHandle(ref, () => ({
    flushPending: async () => {
      await flushPendingRef.current();
      if (hasUnsavedMarksRef.current) throw new Error(CHECKLIST_UNSAVED_MARKS_MESSAGE);
    },
    scrollToGroup: navigateToGroup,
    setAllGroupsExpanded,
    scrollToField: (fieldId: string) => {
      const section = sections.find(section => section.data.some(row => row.cells?.some(field => field.id === fieldId)));
      if (!section) return;
      cancelChipNavigation();
      const navigationEpoch = navigationEpochRef.current;
      openGroup(section.title);
      pendingScrollGroupRef.current = section.title;
      requestAnimationFrame(() => {
        if (navigationEpoch === navigationEpochRef.current) scrollToMeasuredField(fieldId, section.title);
      });
    },
  }), [navigateToGroup, setAllGroupsExpanded, openGroup, scrollToMeasuredField, sections, cancelChipNavigation]);

  useEffect(() => {
    onUnsavedMarkChange?.(hasUnsavedMarks);
  }, [hasUnsavedMarks, onUnsavedMarkChange]);

  // Re-render form whenever external draft content changes (e.g., after draft.set from parent)
  const draftSnapshot = JSON.stringify(draft.peek());
  useEffect(() => {
    const currentDraft = draft.peek();
    const totalCampos = template.length;
    const camposComValor = Object.entries(currentDraft).filter(([id, val]) => !observationFieldId(id) && val !== undefined && val !== null && val !== '').length;
    const pendentes = Object.keys(dirtyRef.current).length;

    ChecklistFieldsFormLogger.Main.info(`[estado] total=${totalCampos} preenchidos=${camposComValor} pendentes=${pendentes}`);

    if (verboseChecklistLogsEnabled) {
      const amostra = Object.entries(currentDraft)
        .filter(([, val]) => val !== undefined && val !== null && val !== '')
        .slice(0, CHECKLIST_FIELD_LOG_SAMPLE_LIMIT)
        .map(([fieldId, val]) => `${fieldId}=${observationFieldId(fieldId) ? `<observação:${val.length}>` : formatValueForLog(val)}`);

      if (amostra.length > 0) {
        ChecklistFieldsFormLogger.Main.debug(`[estado][amostra] ${amostra.join(' | ')}`);
      }
    }

    Object.entries(currentDraft).forEach(([fieldId, valor]) => {
      if (valor !== undefined && valor !== null && valor !== '') {
        missingValueLoggedRef.current.delete(fieldId);
      }
    });

    previousDraftRef.current = { ...currentDraft };
    // Bump version para garantir atualização da SectionList
    setListVersion(v => v + 1);
  }, [draftSnapshot, template, verboseChecklistLogsEnabled]);

  const flushDraft = React.useCallback(async () => {
    if (!persistence.hasPending()) return;
    try {
      do {
        await persistence.flush(async changes => {
          traceImage('form.bulkUpsert.queued', { checklistId, changes: Object.fromEntries(Object.entries(changes).filter(([id]) => !observationFieldId(id))) });
          await db.bulkUpsertChecklistValues(empresaId, checklistId, changes, { skipPendingOp: false });
        }, changes => {
          traceImage('form.bulkUpsert.applied', { checklistId });
          if (mounted.current && currentPersistenceRef.current === persistence) callbacksRef.current.onDraftFlushed?.(changes);
        });
      } while (mounted.current && currentPersistenceRef.current === persistence && persistence.hasPending());
      if (mounted.current && currentPersistenceRef.current === persistence && !persistence.hasPending()) {
        hideSpinner();
        callbacksRef.current.onSaveError?.(null);
      }
    } catch (error) {
      ChecklistFieldsFormLogger.Main.error(`[flushDraft] checklist=${checklistId} campos=${Object.keys(persistence.dirty).join(',')} falha local:`, error);
      if (mounted.current && currentPersistenceRef.current === persistence && persistence.hasPending()) {
        callbacksRef.current.onPendingChange?.(true);
        callbacksRef.current.onSaveError?.('Não foi possível salvar as últimas alterações. Elas continuam nesta tela. Aguarde e tente voltar novamente.');
      }
      throw error;
    }
  }, [persistence, checklistId, empresaId, db, hideSpinner, onDraftFlushed, onPendingChange, onSaveError]);
  flushPendingRef.current = flushDraft;

  useEffect(() => { if (!editable) Keyboard.dismiss(); }, [editable]);

  const handleChange = React.useCallback(
    (campoId: string, value: string, options?: { skipPersistence?: boolean }) => {
      const observedFieldId = observationFieldId(campoId);
      const field = template.find(item => item.id === (observedFieldId ?? campoId));
      if (!editable || !field || field.historical_read_only || (observedFieldId && !canObserve(field))) {
        ChecklistFieldsFormLogger.Main.info(
          `[handleChange] edição bloqueada; ignorando campo=${campoId}`
        );
        return;
      }
      if (draft[campoId].get() === value) return; // evita rodada extra
      if (!observedFieldId) traceImage('form.handleChange', { checklistId, campoId, to: value });
      draft[campoId].set(value); // UI responde instantaneamente
      setListVersion(v => v + 1);

      if (options?.skipPersistence) {
        ChecklistFieldsFormLogger.Main.info(
          `[handleChange] campo=${campoId} marcado como preview-only, ignorando persistência`
        );
        return;
      }
      onSaveError?.(null);
      persistence.change(campoId, value); // marca que mudou

      // CORREÇÃO: Detectar se é uma URL de upload recém-concluído e usar delay maior
      const isImageUpload = Boolean(value) && (
        value.startsWith('https://') && value.includes('checklists-photos/') ||
        value.startsWith('https://') && value.includes('checklists-signatures/') || 
      value.startsWith('https://') && value.includes('checklists-marcacoes/')
    );
    const delayToUse = isImageUpload ? FLUSH_DELAY_WITH_UPLOAD : FLUSH_DELAY;
    ChecklistFieldsFormLogger.Main.info(`[handleChange] campo=${campoId} valor=${observedFieldId ? `<observação:${value.length}>` : formatValueForLog(value)} delay=${delayToUse}ms upload=${isImageUpload}`);

      if (flushTimer.current) clearTimeout(flushTimer.current);
      flushTimer.current = setTimeout(() => { void flushDraft().catch(() => {}); }, delayToUse);
      showSpinner(); // spinner suave
    },
    [draft, editable, template, canObserve, flushDraft, showSpinner, setListVersion, persistence, onSaveError]
  );

  // Memoiza renderItem para reduzir renders
  const renderItem = React.useCallback(({ item, hideLabel = false }: { item: ChecklistField; hideLabel?: boolean }) => {
    // Trata tipos "dropdown*" de forma dinâmica
    if (item.tipo.startsWith('dropdown')) {
      return (
        <ChecklistDropdownField
          checklistId={checklistId}
          fieldId={item.id}
          tipo={item.tipo}
          label={item.nome}
          value={draft[item.id]?.get()}
          onChange={val => handleChange(item.id, val)}
          observation={observationsEnabled ? draft[observationKey(item.id)]?.get() ?? '' : ''}
          onObservationChange={val => handleChange(observationKey(item.id), val)}
          observationEditable={canObserve(item)}
          editable={editable && !item.historical_read_only}
        />
      );
    }

    switch (item.tipo) {
      case 'text': {
        const value = draft[item.id].get();
        const previousValue = previousDraftRef.current[item.id];
        const tinhaValorAntes = previousValue !== undefined && previousValue !== null && previousValue !== '';
        const perdeuValor = tinhaValorAntes && (value === undefined || value === null || value === '');

        if (perdeuValor && !missingValueLoggedRef.current.has(item.id)) {
          ChecklistFieldsFormLogger.Main.warn(`[renderItem-text] valor ausente para campo ${item.id} (antes=${formatValueForLog(previousValue)})`);
          missingValueLoggedRef.current.add(item.id);
        } else if (!perdeuValor && missingValueLoggedRef.current.has(item.id)) {
          missingValueLoggedRef.current.delete(item.id);
        }

        if (verboseChecklistLogsEnabled) {
          ChecklistFieldsFormLogger.Main.debug(`[renderItem-text] campo=${item.id} valor=${formatValueForLog(value)}`);
        }

        if (isGpsAddressField(item)) {
          return (
            <ChecklistGpsAddressField
              scopeId={`${empresaId}:${checklistId}`}
              fieldId={item.id}
              label={item.nome}
              hideLabel={hideLabel}
              editable={editable && !item.historical_read_only}
              gpsEnabled={gpsEnabled}
              value={value ?? ''}
              onChange={val => handleChange(item.id, val)}
            />
          );
        }

        return (
          <TextField
            field={item}
            hideLabel={hideLabel}
            editable={editable && !item.historical_read_only}
            value={value ?? ''}
            onChange={val => handleChange(item.id, val)}
          />
        );
      }
      case 'foto':
      case 'photo': {
        const handlePhotoChange = (val: string) => handleChange(item.id, val, { skipPersistence: true });
        return (
          <View testID={`checklist-photo-row-${item.id}`} style={styles.itemContainer}>
            <ChecklistPhotoField
              empresaId={empresaId}
              checklistId={checklistId}
              fieldId={item.id}
              label={item.nome}
              hideLabel={hideLabel}
              compactCard
              editable={editable && !item.historical_read_only}
              value={draft[item.id]?.get()}
              onChange={handlePhotoChange}
              onPhotoFlowStateChange={onPhotoFlowStateChange}
              maxPhotos={5}
            />
            <ChecklistMediaObservation
              fieldId={item.id}
              label={item.nome}
              value={observationsEnabled ? draft[observationKey(item.id)]?.get() ?? '' : ''}
              editable={canObserve(item)}
              onChange={val => handleChange(observationKey(item.id), val)}
            />
          </View>
        );
      }
      case 'assinatura':
      case 'signature': {
        const handleSignatureChange = (val: string) => handleChange(item.id, val, { skipPersistence: true });
        return (
          <View style={styles.itemContainer}>
            <Text style={styles.label}>{item.nome}</Text>
            <ChecklistSignatureField
              empresaId={empresaId}
              checklistId={checklistId}
              fieldId={item.id}
              value={draft[item.id]?.get()}
              onChange={handleSignatureChange}
            />
          </View>
        );
      }
      case 'marcação':
      case 'marcacao':
      case 'mark': {
        const handleMarkChange = (val: string) => handleChange(item.id, val, { skipPersistence: true });
        const fieldEditable = editable && !item.historical_read_only;
        return (
          <View style={styles.itemContainer}>
            <Text style={styles.label}>{item.nome}</Text>
            <ChecklistMarkField
              empresaId={empresaId}
              checklistId={checklistId}
              fieldId={item.id}
              backgroundFieldId={item.original_campo_id ?? item.id}
              onCaptureStateChange={capturing => {
                if (capturing) capturingFieldsRef.current.add(item.id);
                else {
                  capturingFieldsRef.current.delete(item.id);
                  if (!capturingFieldsRef.current.size && pendingGlobalCollapseRef.current && mounted.current) {
                    setAllGroupsExpanded(false);
                  }
                  if (!template.some(field => field.grupo === item.grupo && capturingFieldsRef.current.has(field.id)) && pendingCollapseGroupsRef.current.delete(item.grupo) && mounted.current) {
                    setExpandedGroups(previous => ({ ...previous, [item.grupo]: false }));
                  }
                }
                if (mounted.current) setCaptureBusy(capturingFieldsRef.current.size > 0);
              }}
              value={draft[item.id]?.get()}
              onChange={handleMarkChange}
              points={draftMarksByField[item.id] ?? []}
              onPointsChange={points => {
                if (!mounted.current || (!fieldEditable && points.length > 0)) return;
                const next = { ...draftMarksByFieldRef.current };
                if (points.length) next[item.id] = points;
                else delete next[item.id];
                draftMarksByFieldRef.current = next;
                const nextHasUnsavedMarks = Object.values(next).some(marks => marks.length > 0);
                hasUnsavedMarksRef.current = nextHasUnsavedMarks;
                onUnsavedMarkChange?.(nextHasUnsavedMarks);
                setDraftMarksByField(next);
              }}
              onInteraction={kind => fieldEditable ? (onMarkInteraction?.(kind) ?? true) : false}
              editable={fieldEditable}
            />
            <ChecklistMediaObservation
              fieldId={item.id}
              label={item.nome}
              value={observationsEnabled ? draft[observationKey(item.id)]?.get() ?? '' : ''}
              editable={canObserve(item)}
              onChange={val => handleChange(observationKey(item.id), val)}
            />
          </View>
        );
      }
      case 'cep1': {
        // Identificar campos correlatos (mesmo grupo) para preenchimento automático
        const sameGroup = template.filter(f => f.grupo === item.grupo);
        const findId = (t: string) => sameGroup.find(f => f.tipo === t)?.id;

        const enderecoId = findId('cep2'); // Endereço (rua)
        const bairroId = findId('cep4');   // Bairro
        const cidadeId = findId('cep5');   // Cidade
        const ufId = findId('cep6');       // UF

        const value = draft[item.id]?.get();

        return (
          <ChecklistCEPField
            field={item}
            hideLabel={hideLabel}
            value={value ?? ''}
            onChangeSelf={val => handleChange(item.id, val)}
            updateField={handleChange}
            enderecoId={enderecoId}
            bairroId={bairroId}
            cidadeId={cidadeId}
            ufId={ufId}
          />
        );
      }
      case 'cep2':
      case 'cep3':
      case 'cep4':
      case 'cep5':
      case 'cep6': {
        // Trata como campo de texto normal
        const value = draft[item.id].get();
        return (
          <TextField
            field={item}
            hideLabel={hideLabel}
            editable={editable && !item.historical_read_only}
            value={value ?? ''}
            onChange={val => handleChange(item.id, val)}
          />
        );
      }
      case 'timestamp with time zone': {
        // Campo de data/hora customizado para web
        return (
          <ChecklistDateTimeField
            field={item}
            value={draft[item.id]?.get()}
            onChange={val => handleChange(item.id, val)}
          />
        );
      }
      case 'number': {
        const value = draft[item.id].get();
        return (
          <NumberField
            field={item}
            hideLabel={hideLabel}
            editable={editable && !item.historical_read_only}
            value={value ?? ''}
            onChange={val => handleChange(item.id, val)}
          />
        );
      }
      case 'mensagem pronta': {
        const value = draft[item.id]?.get();
        return (
          <View style={styles.itemContainer}>
            <ChecklistReadyMessageField
              field={item}
              value={value}
              onChange={val => handleChange(item.id, val)}
            />
          </View>
        );
      }
      default:
        return null;
    }
  }, [draft, draftMarksByField, handleChange, empresaId, checklistId, onPhotoFlowStateChange, onMarkInteraction, onUnsavedMarkChange, template, verboseChecklistLogsEnabled, editable, gpsEnabled, observationsEnabled, canObserve, setAllGroupsExpanded]);

  useEffect(() => {
    mounted.current = true;
    persistence.start();
    return () => {
      mounted.current = false;
      persistence.stop();
      if (flushTimer.current) clearTimeout(flushTimer.current);
      if (spinnerTimer.current) clearTimeout(spinnerTimer.current);
      if (chipJumpTimerRef.current) clearTimeout(chipJumpTimerRef.current);
    };
  }, [persistence]);

  return (
    <View style={styles.formRoot}>
    <View style={styles.chipBar}>
      <ScrollView ref={chipsRef} horizontal showsHorizontalScrollIndicator={false} keyboardShouldPersistTaps="handled" contentContainerStyle={styles.chips}>
        {sections.map(section => {
          const fields = section.data.flatMap(row => row.cells ?? [row]);
          const selected = section.title === activeGroup;
          const label = section.title.replace(/^Dados (?:do|da|de)\s+/i, '').replace(/^Local de\s+/i, '');
          return <TouchableOpacity key={section.title} accessibilityRole="button" accessibilityLabel={`Ir para ${section.title}`}
            accessibilityState={{ selected, disabled: captureBusy }} disabled={captureBusy} onPress={() => navigateToGroup(section.title)}
            onLayout={event => { chipOffsetsRef.current[section.title] = event.nativeEvent.layout.x; }}
            style={[styles.chip, selected && styles.chipSelected]}>
            <Text style={[styles.chipText, selected && styles.chipTextSelected]}>{label}</Text>
            {fields.length > 0 && countChecklistFilledFields(fields, draft.peek()) === fields.length && <Ionicons name="checkmark" size={14} color={selected ? '#fff' : theme.colors.util.success} />}
          </TouchableOpacity>;
        })}
      </ScrollView>
    </View>
    <ScrollView ref={scrollViewRef} contentContainerStyle={styles.container}
      {...webScrollIntentProps} onScrollBeginDrag={cancelChipNavigation}
      keyboardShouldPersistTaps="handled" scrollEnabled={!captureBusy} onScroll={handleIosScroll} scrollEventThrottle={16}
      onLayout={event => { viewportHeightRef.current = event.nativeEvent.layout.height; updateScrollGroups(); }}
      onContentSizeChange={(_width, height) => { contentHeightRef.current = height; updateScrollGroups(); }}>
      {sections.map((section, index) => {
        const fields = section.data.flatMap(row => row.cells ?? [row]);
        const expanded = expandedGroups[section.title] !== false;
        return <View key={section.title} testID={`checklist-section-${section.title}`} style={styles.sectionCard}
          onLayout={event => {
            groupHeightsRef.current[section.title] = event.nativeEvent.layout.height;
            handleIosSectionLayout(section.title, event.nativeEvent.layout.y);
            updateScrollGroups();
          }}>
          <View ref={node => setIosSectionHeaderRef(section.title, node)} collapsable={false}>
            <TouchableOpacity testID={`checklist-group-${section.title}`} accessibilityRole="button"
              accessibilityLabel={`Grupo ${section.title}`} accessibilityState={{ expanded, disabled: captureBusy }} disabled={captureBusy}
              style={styles.accordionHeader}
              onLayout={event => { headerHeightsRef.current[section.title] = event.nativeEvent.layout.height; updateScrollGroups(); }}
              onPress={() => {
                Keyboard.dismiss();
                cancelChipNavigation();
                // A captura precisa da geometria visível; recolher é adiado até obter a imagem.
                if (expanded && fields.some(field => capturingFieldsRef.current.has(field.id))) {
                  if (!pendingCollapseGroupsRef.current.delete(section.title)) pendingCollapseGroupsRef.current.add(section.title);
                  return;
                }
                pendingCollapseGroupsRef.current.delete(section.title);
                setExpandedGroups(previous => ({ ...previous, [section.title]: previous[section.title] === false }));
              }}>
              <View style={styles.groupNumber}><Text style={styles.groupNumberText}>{index + 1}</Text></View>
              <View style={styles.groupCopy}><Text style={styles.header}>{section.title}</Text></View>
              <Text style={styles.groupCount}>{countChecklistFilledFields(fields, draft.peek())}/{fields.length}</Text>
              <Ionicons name={expanded ? 'chevron-up' : 'chevron-down'} size={18} color={theme.colors.text.secondary} />
            </TouchableOpacity>
          </View>
          {/* Recolher não desmonta mídia, uploads ou rascunhos já iniciados. */}
          <View testID={`checklist-group-body-${section.title}`}
            style={[styles.sectionBody, !expanded && styles.collapsedBody]}
            accessibilityElementsHidden={!expanded} importantForAccessibility={expanded ? 'auto' : 'no-hide-descendants'}>
            <View style={styles.fieldsGrid}>
              {fields.map(field => {
                const row = section.data.find(item => item.cells?.some(cell => cell.id === field.id))!;
                const paired = (row.cells?.length ?? 1) === 2;
                const ownLabel = ['text', 'number', 'foto', 'photo'].includes(field.tipo) || /^cep[1-6]$/.test(field.tipo);
                const pairedHeight = paired ? Math.max(18, ...(row.cells ?? []).map(cell => labelHeights[cell.id] ?? 0)) : undefined;
                return <View key={field.id} testID={`checklist-cell-${field.id}`} collapsable={false}
                  ref={node => { if (node) fieldNodesRef.current[field.id] = node; else delete fieldNodesRef.current[field.id]; }}
                  style={{ width: paired ? '50%' : '100%', paddingHorizontal: 5 }}
                  pointerEvents={(editable && !field.historical_read_only) ||
                    ['foto', 'photo'].includes(field.tipo) ||
                    (['marcação', 'marcacao', 'mark'].includes(field.tipo) && Boolean(draftMarksByField[field.id]?.length)) ? 'auto' : 'none'}>
                  {ownLabel && <View style={[styles.fieldLabel, paired && { minHeight: pairedHeight }]}>
                    <Text style={styles.gridLabel} onLayout={event => {
                      const nextHeight = Math.ceil(event.nativeEvent.layout.height);
                      if (nextHeight > 0) setLabelHeights(previous => previous[field.id] === nextHeight ? previous : { ...previous, [field.id]: nextHeight });
                    }}>{field.nome}</Text>
                  </View>}
                  {renderItem({ item: field, hideLabel: ownLabel })}
                </View>;
              })}
            </View>
          </View>
        </View>;
      })}
    </ScrollView>
    </View>
  );
});

ChecklistFieldsForm.displayName = 'ChecklistFieldsForm';

// Estilos minimalistas e elegantes
const styles = StyleSheet.create({
  formRoot: { flex: 1 },
  chipBar: { backgroundColor: theme.colors.background.primary, borderBottomWidth: 1, borderBottomColor: theme.colors.border.light },
  chips: { paddingHorizontal: 12, paddingVertical: 7, gap: 7 },
  chip: { minHeight: 44, paddingHorizontal: 14, borderRadius: 22, borderWidth: 1, borderColor: '#DBE2F0', backgroundColor: '#F3F6FC', alignItems: 'center', justifyContent: 'center', flexDirection: 'row', gap: 5 },
  chipSelected: { backgroundColor: theme.colors.primary.default, borderColor: theme.colors.primary.default },
  chipText: { color: '#465268', fontSize: 13, fontWeight: '500' }, chipTextSelected: { color: '#fff', fontWeight: '700' },
  fieldsGrid: { flexDirection: 'row', flexWrap: 'wrap', marginHorizontal: -5, alignItems: 'flex-start' },
  fieldLabel: { justifyContent: 'flex-end', marginBottom: 4 },
  gridLabel: { fontSize: 13, lineHeight: 18, color: theme.colors.text.secondary },
  sectionCard: { backgroundColor: theme.colors.background.primary, borderWidth: 1, borderColor: theme.colors.border.light, borderRadius: 14, marginBottom: 8, overflow: 'hidden' },
  accordionHeader: { minHeight: 48, flexDirection: 'row', alignItems: 'center', gap: 9, paddingHorizontal: 13, paddingVertical: 8 },
  groupNumber: { width: 28, height: 28, borderRadius: 14, backgroundColor: theme.colors.background.secondary, alignItems: 'center', justifyContent: 'center' },
  groupNumberText: { fontSize: theme.fontSizes.xs, fontWeight: theme.fontWeights.semibold as '600', color: theme.colors.primary.default },
  groupCopy: { flex: 1, minWidth: 0 },
  groupCount: { fontSize: theme.fontSizes.xs, color: theme.colors.text.secondary },
  sectionBody: { paddingHorizontal: 13, paddingBottom: 0 },
  collapsedBody: { display: 'none' },
  cell: { flex: 1, minWidth: 0 },
  container: {
    paddingHorizontal: 12,
    paddingTop: 8,
    paddingBottom: 16,
    backgroundColor: theme.colors.background.secondary,
  },
  header: {
    fontWeight: 'bold',
    fontSize: 14,
    color: theme.colors.text.primary,
  },
  itemContainer: {
    marginBottom: 10,
    padding: 0,
  },
  label: {
    fontSize: 13,
    color: theme.colors.text.secondary,
    marginBottom: 4,
  },
  input: {
    backgroundColor: theme.colors.background.primary,
    borderRadius: theme.borderRadius.sm,
    borderWidth: 1,
    borderColor: theme.colors.border.light,
    padding: theme.spacing.sm,
    fontSize: theme.fontSizes.sm,
    color: theme.colors.text.primary,
  },
}); 
